import type { CalendarEventData } from '@shared/core/add-to-calendar'
import type { LocationType } from '@shared/core/appointment-types'
import {
  buildBookingCalendarEventData,
  describeBookingLocation,
  MEET_GENERIC_LINK_TEXT,
  MEET_VIDEO_CALL_LABEL,
  type PublicBookingDto,
} from '@shared/core/bookings'
import type { BookingCancellationPolicyDto } from '@shared/core/cancellation-policy'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import type { BookingData } from 'src/modules/booking/containers/booking-wrapper/booking-wrapper.interface'
import type {
  AppointmentTypeForConfirmed,
  BookingConfirmedDisplayData,
  ConfirmedLocationRow,
} from './booking-confirmed-step.interface'

const GOOGLE_MAPS_SEARCH_URL = 'https://www.google.com/maps/search/?api=1&query='

const LOCATION_HREF_BUILDERS: Partial<Record<LocationType, (text: string) => string | undefined>> = {
  in_person: (address) => `${GOOGLE_MAPS_SEARCH_URL}${encodeURIComponent(address)}`,
  google_meet: (text) => (text === MEET_GENERIC_LINK_TEXT ? undefined : text),
}

/** No row for `none` (or an older booking without a type), so an untyped booking's page is unchanged. */
export function buildConfirmedLocationRow(
  booking: PublicBookingDto | null | undefined,
  customerAddress: string | null,
): ConfirmedLocationRow | null {
  if (!booking?.location_type || booking.location_type === 'none') return null
  const locationType = booking.location_type
  const text = describeBookingLocation(
    {
      locationType,
      location: resolveConfirmedLocation(booking, customerAddress),
      meetLinkStatus: booking.meet_link_status,
    },
    'client',
  )
  if (!text) return null
  return {
    label: locationType === 'google_meet' ? MEET_VIDEO_CALL_LABEL : undefined,
    text,
    href: LOCATION_HREF_BUILDERS[locationType]?.(text),
  }
}

/** The public read never carries a client's address, so that type takes the one handed over by the details step. */
function resolveConfirmedLocation(
  booking: PublicBookingDto,
  customerAddress: string | null,
): string | null | undefined {
  return booking.location_type === 'customer_address' ? customerAddress : booking.location
}

export function hasBookingData(bookingData?: BookingData | null): boolean {
  return !!(bookingData && (bookingData.selectedDate || bookingData.selectedTime))
}

export function getDisplayDataFromBookingData(bookingData: BookingData): BookingConfirmedDisplayData {
  return {
    selectedDate: bookingData.selectedDate ?? null,
    selectedTime: bookingData.selectedTime ?? null,
    endTime: null,
    timezone: bookingData.timezone ?? null,
    appointmentType: bookingData.appointmentType ?? null,
  }
}

/** Wide enough for client/server clock skew; a revisit after it gets no "on its way" line. */
const FRESH_BOOKING_WINDOW_MS = 60 * 60 * 1000

/** Date and time both come from the server's display zone; the browser zone would shift the weekday. */
export function formatCancelBy(policy: BookingCancellationPolicyDto): string {
  return formatInTimeZone(policy.cancel_by, policy.display_timezone, "EEEE, MMMM d 'at' h:mm a")
}

export function isFreshBooking(booking: PublicBookingDto | null | undefined, now: Date = new Date()): boolean {
  if (!booking?.created_at) return false
  const createdAt = new Date(booking.created_at as unknown as string).getTime()
  return now.getTime() - createdAt <= FRESH_BOOKING_WINDOW_MS
}

export function getDisplayDataFromBooking(booking: PublicBookingDto): BookingConfirmedDisplayData {
  const customerTz = booking.customer_tz || booking.provider_tz || 'UTC'
  const startDate = new Date(booking.start_time_utc as unknown as string)
  const endDate = new Date(booking.end_time_utc as unknown as string)
  const selectedTime = formatInTimeZone(startDate, customerTz, 'h:mm a')
  const endTime = formatInTimeZone(endDate, customerTz, 'h:mm a')
  return {
    selectedDate: startDate,
    selectedTime,
    endTime,
    timezone: customerTz,
    appointmentType: booking.appointment_type_id,
  }
}

export function getEmptyDisplayData(): BookingConfirmedDisplayData {
  return {
    selectedDate: null,
    selectedTime: null,
    endTime: null,
    timezone: null,
    appointmentType: null,
  }
}

export function findAppointmentType(
  types: AppointmentTypeForConfirmed[],
  id?: string | null,
): AppointmentTypeForConfirmed | null {
  if (!id || !types.length) return null
  const found = types.find((t) => t.id === id)
  return found ?? null
}

/** Compute end time string from start time and duration, e.g. "2:30 PM" + 30 → "3:00 PM". */
export function computeEndTimeDisplay(selectedTime: string, durationMinutes: number): string {
  const [time, period] = selectedTime.split(' ')
  const [hours, minutes] = time.split(':').map(Number)
  let nextHours = hours
  let nextMinutes = minutes + durationMinutes
  if (nextMinutes >= 60) {
    nextHours += Math.floor(nextMinutes / 60)
    nextMinutes = nextMinutes % 60
  }
  return `${nextHours}:${nextMinutes.toString().padStart(2, '0')} ${period}`
}

/** Time range label for UI, e.g. "2:30 PM - 3:00 PM (America/New_York)". */
export function getTimeRangeLabel(selectedTime: string, endTime: string, timezone: string | null): string {
  const suffix = timezone ? ` (${timezone})` : ''
  return `${selectedTime} - ${endTime}${suffix}`
}

/** Build calendar event from API booking. */
function buildCalendarEventFromBooking(params: {
  booking: PublicBookingDto
  appointmentType: AppointmentTypeForConfirmed
  providerName: string
  customerAddress: string | null
  canReschedule: boolean
}): CalendarEventData {
  const { booking, appointmentType, providerName, customerAddress, canReschedule } = params
  const descriptionParts = [
    `Appointment Type: ${appointmentType.name} (${appointmentType.duration_minutes} min)`,
    `Provider: ${providerName}`,
    '',
    `Customer: ${booking.customer_name}`,
    `Phone: ${booking.customer_phone}`,
    '',
    canReschedule
      ? `Reschedule your booking: ${import.meta.env.VITE_WEBSITE_BASE_URL}/bookings/${booking.id}`
      : `If you need to reschedule, please contact ${providerName} directly`,
  ]
  return buildBookingCalendarEventData({
    bookingId: booking.id,
    appointmentTypeName: appointmentType.name,
    providerName,
    startTime: new Date(booking.start_time_utc as unknown as string),
    endTime: new Date(booking.end_time_utc as unknown as string),
    description: descriptionParts.join('\n'),
    location: resolveConfirmedLocation(booking, customerAddress),
  })
}

/** Parse "2:30 PM" in a timezone into UTC Date. */
function parseDisplayTimeToUtc(
  selectedDate: Date,
  selectedTime: string,
  timezone: string,
  durationMinutes: number,
): { startTime: Date; endTime: Date } {
  const [time, period] = selectedTime.split(' ')
  const [hours, minutes] = time.split(':').map(Number)
  let startHours = hours
  if (period === 'PM' && hours !== 12) startHours += 12
  else if (period === 'AM' && hours === 12) startHours = 0

  const year = selectedDate.getFullYear()
  const month = selectedDate.getMonth()
  const day = selectedDate.getDate()

  const startTime =
    timezone !== 'UTC'
      ? fromZonedTime(new Date(year, month, day, startHours, minutes, 0), timezone)
      : new Date(Date.UTC(year, month, day, startHours, minutes, 0))
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000)
  return { startTime, endTime }
}

/** Build calendar event from display data (no API booking). */
function buildCalendarEventFromDisplayData(params: {
  bookingId: string
  displayData: BookingConfirmedDisplayData
  appointmentType: AppointmentTypeForConfirmed
  providerName: string
  bookingData?: BookingData | null
}): CalendarEventData {
  const { bookingId, displayData, appointmentType, providerName, bookingData } = params
  const timezone = displayData.timezone || 'UTC'
  const durationMinutes = appointmentType.duration_minutes || 30
  const { startTime, endTime } = parseDisplayTimeToUtc(
    displayData.selectedDate!,
    displayData.selectedTime!,
    timezone,
    durationMinutes,
  )
  const descriptionParts = [
    `Appointment Type: ${appointmentType.name} (${appointmentType.duration_minutes} min)`,
    `Provider: ${providerName}`,
  ]
  if (bookingData?.fullName) descriptionParts.push(`Customer: ${bookingData.fullName}`)
  if (bookingData?.phoneNumber) descriptionParts.push(`Phone: ${bookingData.phoneNumber}`)
  return buildBookingCalendarEventData({
    bookingId,
    appointmentTypeName: appointmentType.name,
    providerName,
    startTime,
    endTime,
    description: descriptionParts.join('\n'),
  })
}

export function buildCalendarEventData(params: {
  bookingId: string
  booking: PublicBookingDto | null
  bookingData?: BookingData | null
  displayData: BookingConfirmedDisplayData
  appointmentType: AppointmentTypeForConfirmed | null
  providerName: string
  customerAddress?: string | null
  /** The server's can-reschedule answer; false while it loads or after an error. */
  canReschedule: boolean
}): CalendarEventData | null {
  const {
    bookingId,
    booking,
    bookingData,
    displayData,
    appointmentType,
    providerName,
    customerAddress,
    canReschedule,
  } = params
  if (!displayData.selectedDate || !appointmentType) return null
  if (booking) {
    return buildCalendarEventFromBooking({
      booking,
      appointmentType,
      providerName,
      customerAddress: customerAddress ?? null,
      canReschedule,
    })
  }
  const id = bookingData?.id ?? bookingId
  if (displayData.selectedTime && id) {
    return buildCalendarEventFromDisplayData({ bookingId: id, displayData, appointmentType, providerName, bookingData })
  }
  return null
}
